import { defineStore } from 'pinia'
import { ref, shallowRef, computed } from 'vue'
import * as monaco from 'monaco-editor'
import { readFile, writeFile } from '@/services/fileService'
import { formatFile } from '@/services/buildService'

export interface OpenFile {
  path:     string
  content:  string
  isDirty:  boolean
  language: string
}

export interface EditorColumn {
  id:         string
  openFiles:  OpenFile[]
  activeFile: OpenFile | null
}

function detectLanguage(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() ?? ''
  const map: Record<string, string> = {
    twig: 'twig',    php: 'php',        js: 'javascript', ts: 'typescript',
    css:  'css',     less: 'less',      json: 'json',     yaml: 'yaml',
    yml:  'yaml',    html: 'html',      env: 'plaintext', md: 'markdown',
    vue:  'html',    xml: 'xml',        sh: 'shell',      bash: 'shell',
    sql:  'sql',     graphql: 'graphql', gql: 'graphql',
  }
  return map[ext] ?? 'plaintext'
}

function makeColumnId(): string {
  return `col-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export const useEditorStore = defineStore('editor', () => {
  // Non-reactive: Monaco objects must never be wrapped in Vue proxies
  const models          = new Map<string, monaco.editor.ITextModel>()
  const editorInstances = new Map<string, monaco.editor.IStandaloneCodeEditor>()

  // Reactive columns — each has its own open-file list and active tab
  const columns       = ref<EditorColumn[]>([{ id: makeColumnId(), openFiles: [], activeFile: null }])
  const activeColumnId = ref<string>(columns.value[0].id)

  const saveError = ref<string | null>(null)

  // Tracks registrations so editorInstance computed stays reactive
  const _instanceVersion = ref(0)

  // ── Backwards-compatible single-editor shallowRef ────────────────────────────
  // ClaudeSidebar / ClaudeChat watch this to detect when the editor is ready.
  const editorInstance = shallowRef<monaco.editor.IStandaloneCodeEditor | null>(null)

  // ── Computed ──────────────────────────────────────────────────────────────────

  /** Active column object. Always defined — falls back to first column. */
  const activeColumn = computed(() =>
    columns.value.find(c => c.id === activeColumnId.value) ?? columns.value[0]
  )

  /**
   * Writable computed for backwards compatibility.
   * Reading: returns activeColumn.activeFile.
   * Writing: sets it on the active column (e.g. ClaudeChat sets this directly).
   */
  const activeFile = computed<OpenFile | null>({
    get: () => activeColumn.value?.activeFile ?? null,
    set: (file) => {
      const col = activeColumn.value
      if (col) col.activeFile = file
    },
  })

  /**
   * All unique open files across all columns.
   * Used by CommandPalette and ClaudeChat when they list or search open files.
   */
  const openFiles = computed<OpenFile[]>(() => {
    const seen   = new Set<string>()
    const result: OpenFile[] = []
    for (const col of columns.value) {
      for (const f of col.openFiles) {
        if (!seen.has(f.path)) {
          seen.add(f.path)
          result.push(f)
        }
      }
    }
    return result
  })

  // ── Column management ─────────────────────────────────────────────────────────

  function setActiveColumn(id: string): void {
    activeColumnId.value = id
    editorInstance.value = editorInstances.get(id) ?? null
  }

  function addColumn(): void {
    if (columns.value.length >= 10) return
    const id = makeColumnId()
    columns.value.push({ id, openFiles: [], activeFile: null })
    setActiveColumn(id)
  }

  function removeColumn(id: string): void {
    if (columns.value.length <= 1) return

    const col = columns.value.find(c => c.id === id)
    if (col) {
      col.openFiles.forEach(f => {
        const openElsewhere = columns.value
          .filter(c => c.id !== id)
          .some(c => c.openFiles.some(of => of.path === f.path))
        if (!openElsewhere) {
          models.get(f.path)?.dispose()
          models.delete(f.path)
        }
      })
    }

    editorInstances.get(id)?.dispose()
    editorInstances.delete(id)
    columns.value = columns.value.filter(c => c.id !== id)

    if (activeColumnId.value === id) {
      setActiveColumn(columns.value[columns.value.length - 1].id)
    }
  }

  // ── Editor instance registry ─────────────────────────────────────────────────

  function setEditorInstance(
    columnId: string,
    editor: monaco.editor.IStandaloneCodeEditor,
  ): void {
    editorInstances.set(columnId, editor)
    _instanceVersion.value++
    if (columnId === activeColumnId.value) {
      editorInstance.value = editor
    }
  }

  function getEditorInstance(
    columnId: string,
  ): monaco.editor.IStandaloneCodeEditor | undefined {
    return editorInstances.get(columnId)
  }

  // ── Model access ──────────────────────────────────────────────────────────────

  function getModel(path: string): monaco.editor.ITextModel | undefined {
    return models.get(path)
  }

  // ── File operations ───────────────────────────────────────────────────────────

  /** Open a file in a specific column (defaults to the active column). */
  async function openFile(path: string, columnId?: string): Promise<void> {
    const colId = columnId ?? activeColumnId.value
    const col   = columns.value.find(c => c.id === colId)
    if (!col) return

    const existing = col.openFiles.find(f => f.path === path)
    if (existing) {
      col.activeFile = existing
      return
    }

    const content  = await readFile(path)
    const language = detectLanguage(path)
    const file: OpenFile = { path, content, isDirty: false, language }

    // Create a Monaco model only once per path; reused across columns
    if (!models.has(path)) {
      models.set(path, monaco.editor.createModel(content, language))
    }

    col.openFiles.push(file)
    col.activeFile = file
  }

  /** Save the active file in a column to the server. */
  async function saveCurrentFile(newContent: string, columnId?: string): Promise<void> {
    const colId = columnId ?? activeColumnId.value
    const col   = columns.value.find(c => c.id === colId)
    if (!col?.activeFile) return

    saveError.value = null
    const path = col.activeFile.path
    try {
      await writeFile(path, newContent)
      // Mark clean in every column that has this file open
      columns.value.forEach(c => {
        const f = c.openFiles.find(f => f.path === path)
        if (f) { f.content = newContent; f.isDirty = false }
      })
    } catch (err) {
      saveError.value = String(err)
      throw err
    }
  }

  /** Format the active file in a column via Prettier. */
  async function formatCurrentFile(columnId?: string): Promise<void> {
    const colId  = columnId ?? activeColumnId.value
    const col    = columns.value.find(c => c.id === colId)
    const editor = editorInstances.get(colId)
    if (!col?.activeFile || !editor) return

    const current   = editor.getValue()
    const formatted = await formatFile(current, col.activeFile.path)
    if (formatted === current) return

    const model = editor.getModel()
    if (!model) return
    model.pushEditOperations(
      null,
      [{ range: model.getFullModelRange(), text: formatted }],
      () => null,
    )
  }

  /** Close a tab in a column. */
  function closeFile(path: string, columnId?: string): void {
    const colId = columnId ?? activeColumnId.value
    const col   = columns.value.find(c => c.id === colId)
    if (!col) return

    const idx = col.openFiles.findIndex(f => f.path === path)
    if (idx === -1) return

    col.openFiles.splice(idx, 1)

    // Dispose model only when no column has this file open anymore
    const openElsewhere = columns.value
      .filter(c => c.id !== colId)
      .some(c => c.openFiles.some(f => f.path === path))
    if (!openElsewhere && col.openFiles.every(f => f.path !== path)) {
      models.get(path)?.dispose()
      models.delete(path)
    }

    if (col.activeFile?.path === path) {
      col.activeFile = col.openFiles[idx] ?? col.openFiles[idx - 1] ?? null
    }
  }

  /** Mark the file as having unsaved changes in every column where it is open. */
  function markDirty(path: string): void {
    columns.value.forEach(col => {
      const file = col.openFiles.find(f => f.path === path)
      if (file) file.isDirty = true
    })
  }

  return {
    // State
    columns,
    activeColumnId,
    saveError,
    // Computed (backwards-compatible)
    activeFile,
    openFiles,
    editorInstance,
    // Column actions
    setActiveColumn,
    addColumn,
    removeColumn,
    // Editor instance management
    setEditorInstance,
    getEditorInstance,
    // Model
    getModel,
    // File actions
    openFile,
    saveCurrentFile,
    formatCurrentFile,
    closeFile,
    markDirty,
  }
})
